import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import type { OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import GeoContext from 'contexts/geoContext'
import moment from 'moment'
import { getDisplayTotalPricePerNight, getDisplayTotalPrice, aggregateBedbankMultiRoomPrices } from 'lib/hotels/bedbankUtils'
import { useAppDispatch } from 'hooks/reduxHooks'
import { setCustomerValue } from 'actions/AuthActions'
import useBedbankDiscount from 'hooks/useBedbankDiscount'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import { generateLoyaltyPointsCalculatorBedbankOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import * as Analytics from 'analytics/analytics'
import { useQsQueryParams } from 'hooks/useQueryParams'
import { shouldShowPayAtPropertyPill, shouldShowPayLaterPill } from '../../utils'
import { arrayToObject } from 'lib/array/arrayUtils'
import BedbankSummaryPriceDetailsView from './BedbankSummaryPriceDetailsView'
import type { RoomWithData } from 'hooks/OfferPage/useBedbankBuyCallbacks'

interface Props {
  roomsWithData: Array<RoomWithData>
  /**
   * rate here is lowest rate within the offer
   */
  rooms: Array<App.Occupants>
  offer: App.BedbankOffer
  flightOrigin?: App.Airport
  flightPrice?: number
  duration: number
  checkIn?: string
  departureTooEarly?: boolean
  flightsEnabled: boolean
  onViewPackageOptions?: () => void
  promotion?: boolean
  alignment?: 'center' | 'start'
  luxPlusLPPEnabled: boolean
  priceTrackedRef?: React.RefObject<boolean>
  isFlightsSelected?: boolean
}

function BedbankSummaryPriceDetailsIndividualRooms(props: Props) {
  const {
    roomsWithData,
    rooms,
    offer,
    checkIn,
    flightOrigin,
    flightPrice,
    departureTooEarly,
    flightsEnabled,
    duration,
    onViewPackageOptions,
    promotion = false,
    alignment = 'center',
    luxPlusLPPEnabled,
    priceTrackedRef,
    isFlightsSelected,
  } = props

  const { currentRegionCode } = useContext(GeoContext)
  const offerPageDispatch = useOfferPageDispatch()
  const appDispatch = useAppDispatch()
  const hasFlights = !!flightPrice

  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterType })
  }, [offerPageDispatch])

  const {
    propertyFees,
    hotelPrice,
    hotelMemberPrice,
    hotelValue,
    bundlePropertyFees,
    bundlePrice,
    bundleMemberPrice,
    bundleValue,
    taxesAndFees,
    bundleTaxesAndFees,
  } = useMemo(
    () => aggregateBedbankMultiRoomPrices(roomsWithData),
    [roomsWithData],
  )

  const totalsByRoom = useMemo(() => arrayToObject(
    roomsWithData,
    roomData => roomData.room.id,
    roomData => {
      const rate = roomData.rate
      const bundleRate = roomData.bundleRate
      const roomPropertyFees = rate.totals.propertyFees ?? 0
      const roomBundlePropertyFees = bundleRate?.totals.propertyFees || roomPropertyFees

      const roomHotelPrice = rate.totals.inclusive
      const roomHotelMemberPrice = rate.totals.memberInclusive || rate.totals.inclusive
      const roomBundlePrice = bundleRate?.totals.inclusive || roomHotelPrice

      return getDisplayTotalPrice({
        hotelPrice: roomHotelPrice + roomPropertyFees,
        hotelMemberPrice: roomHotelMemberPrice > 0 ? roomHotelMemberPrice + roomPropertyFees : 0,
        hotelValue: rate.value,
        bundlePrice: roomBundlePrice + roomBundlePropertyFees,
        bundleValue: bundleRate?.value,
        checkIn,
        flightPrice: flightPrice ? flightPrice / roomsWithData.length : undefined,
        rooms: [roomData.room],
      })
    },
  ), [checkIn, flightPrice, roomsWithData])

  const departureTooFarInFuture = useMemo(() => checkIn ? moment(checkIn).diff(moment.utc(), 'days') > 330 : false, [checkIn])

  const saleUnit = offer.displayPricingAsPerPerson ? 'person' : 'room'
  const promoteAsBundle = flightsEnabled && !!flightPrice && offer.promoteAsBundle

  const baseTotalPrices = getDisplayTotalPrice({
    hotelPrice: hotelPrice + propertyFees,
    hotelMemberPrice: hotelMemberPrice > 0 ? hotelMemberPrice + propertyFees : 0,
    hotelValue,
    bundlePrice: bundlePrice + bundlePropertyFees,
    bundleValue,
    checkIn,
    flightPrice,
    rooms,
    displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
  })
  const totalPricesPerNight = getDisplayTotalPricePerNight(baseTotalPrices, duration || 1)
  const showOneNightPrice = !promotion && !offer.displayPricingAsPerPerson && !checkIn && !!duration && duration > 1
  const totalPrices = showOneNightPrice ? totalPricesPerNight : baseTotalPrices
  const nightsToDisplay = showOneNightPrice ? 1 : duration

  const offerLocations: Array<string> = useMemo(() => {
    const locations = [
      offer.property?.address.city,
      offer.property?.address.stateProvinceName || '',
      offer.property?.address.countryName]
      .filter(Boolean)

    return locations
  }, [offer])

  const discount = promoteAsBundle ? totalPrices.original.discountWithFlight : totalPrices.original.discount
  const memberDiscount = promoteAsBundle ? totalPrices.original.memberDiscountWithFlight : totalPrices.original.memberDiscount
  const displayDiscount = useBedbankDiscount(discount, memberDiscount)

  useEffect(() => {
    appDispatch(setCustomerValue(totalPrices.totals.price))
  }, [appDispatch, totalPrices.totals.price])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    return [
      ...roomsWithData.map(roomData => {
        const roomTotalPrices = totalsByRoom[roomData.room.id]

        return (
          generateLoyaltyPointsCalculatorBedbankOptions(roomData.rate, {
            price: roomTotalPrices.totals.price,
            memberPrice: roomTotalPrices.totals.memberPrice,
            propertyFees: roomData.rate.totals.propertyFees ?? 0,
            propertyId: offer.property.id,
            offerId: offer.id,
            propertyCountryCode: offer.property.address.countryCode,
            currentRegionCode,
          })
        )
      }),
    ]
  }, [
    totalsByRoom,
    roomsWithData,
    offer,
    currentRegionCode,
  ])

  const showHotelPriceWithFlights = !!flightsEnabled && hasFlights && !offer.promoteAsBundle
  const priceToTrack = showHotelPriceWithFlights ?
    totalPrices?.totals?.price :
    promoteAsBundle ? totalPrices?.totals?.flightBundlePrice : totalPrices?.totals?.price

  const shouldTrackPrice = !!(showHotelPriceWithFlights ? totalPrices?.totals?.price : totalPrices?.totals?.flightBundlePrice || totalPrices?.totals?.price)
  const { searchId } = useQsQueryParams<{ searchId?: string }>()

  useEffect(() => {
    if (shouldTrackPrice && !!priceToTrack && searchId && offer?.id && priceTrackedRef && !priceTrackedRef.current) {
      Analytics.trackClientEvent({
        subject: `searchId:${searchId}_offerId:${offer.id}`,
        action: priceToTrack.toString(),
        category: 'price_accuracy',
        type: 'operational',
      })
      priceTrackedRef.current = true
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldTrackPrice, priceToTrack, offer?.id])

  const canPayLater = roomsWithData.length === 1 && shouldShowPayLaterPill(roomsWithData[0].rate)
  const showPayAtPropertyPill = !isFlightsSelected && roomsWithData.length === 1 && shouldShowPayAtPropertyPill(roomsWithData[0].rate)

  return <BedbankSummaryPriceDetailsView
    rooms={rooms}
    offer={offer}
    flightOrigin={flightOrigin}
    flightPrice={flightPrice}
    duration={duration}
    departureTooEarly={departureTooEarly}
    flightsEnabled={flightsEnabled}
    onViewPackageOptions={onViewPackageOptions}
    onToggleFilter={onToggleFilter}
    alignment={alignment}
    luxPlusLPPEnabled={luxPlusLPPEnabled}
    showPayAtPropertyPill={showPayAtPropertyPill}
    showHotelPriceWithFlights={showHotelPriceWithFlights}
    canPayLater={canPayLater}
    displayDiscount={displayDiscount}
    nightsToDisplay={nightsToDisplay}
    totalPrices={totalPrices}
    saleUnit={saleUnit}
    taxesAndFees={taxesAndFees}
    propertyFees={propertyFees}
    hotelPrice={hotelPrice}
    hotelMemberPrice={hotelMemberPrice}
    offerLocations={offerLocations}
    hotelValue={hotelValue}
    bundlePropertyFees={bundlePropertyFees}
    bundlePrice={bundlePrice}
    bundleMemberPrice={bundleMemberPrice}
    bundleTaxesAndFees={bundleTaxesAndFees}
    pointsEarnCalculationRequests={pointsEarnCalculationRequests}
    hasFlights={hasFlights}
    departureTooFarInFuture={departureTooFarInFuture}
    promoteAsBundle={promoteAsBundle}
  />
}

export default withOfferPageAndReduxState((state, appState) => ({
  flightOrigin: state.airport,
  checkIn: state.checkInDate,
  luxPlusLPPEnabled: getIsLuxPlusLPPEnabled(appState),
  isFlightsSelected: state.flights,
}), BedbankSummaryPriceDetailsIndividualRooms)
